
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Upload, Eye, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { AverageTeamCost } from '@/components/common/AverageTeamCost';
import { TeamEquipmentCost } from '@/components/common/TeamEquipmentCost';
import { TeamGuardEquipmentCost } from '@/components/common/TeamGuardEquipmentCost';
import { useLanguage } from "@/contexts/LanguageContext";

interface Team {
  id: string;
  name: string;
  description?: string | null;
  status?: string | null;
  image_url?: string;
  applications?: Array<{ count: number }>;
}

interface TeamApplicationCardProps {
  team: Team;
  onClick: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  onDeleteClick?: (id: string) => void;
}

export const TeamApplicationCard = ({
  team,
  onClick,
  canEdit = false,
  canDelete = false,
  onDeleteClick
}: TeamApplicationCardProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description || "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  const handleEditClick = () => {
    setName(team.name);
    setDescription(team.description || "");
    setImageFile(null);
    setPreviewUrl(null);
    setIsEditing(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setIsSubmitting(true);
      
      const updateData: Partial<Team> = {
        name,
        description
      };
      
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `team_images/${fileName}`;
        
        console.log("Uploading to storage path:", filePath);
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('teams')
          .upload(filePath, imageFile);
          
        if (uploadError) {
          console.error("Storage upload error:", uploadError);
          throw uploadError;
        }
        
        console.log("Upload successful:", uploadData);
        
        const { data: urlData } = supabase
          .storage
          .from('teams')
          .getPublicUrl(filePath);
          
        if (urlData && urlData.publicUrl) {
          console.log("Public URL generated:", urlData.publicUrl);
          updateData.image_url = urlData.publicUrl;
        } else {
          console.warn("Failed to get public URL for uploaded image");
        }
      }
      
      console.log("Updating team with data:", updateData);
      
      const { error } = await supabase
        .from('teams')
        .update(updateData)
        .eq('id', team.id);
        
      if (error) {
        console.error("Database update error:", error);
        throw error;
      }
      
      toast({
        title: t('teamUpdated'),
        description: t('teamUpdatedSuccessfully')
      });
      
      window.location.reload();
    } catch (error: any) {
      console.error("Complete error:", error);
      toast({
        title: t('failedToUpdate'),
        description: error.message || t('errorUpdatingTeam'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
      setIsEditing(false);
    }
  };

  const imageUrl = team.image_url || "/lovable-uploads/251380b1-4d53-4124-bf15-25f08257a46b.png";

  return (
    <>
      <Card className="group relative overflow-hidden border-0 bg-gradient-to-br from-white via-slate-50/80 to-slate-100/50 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Content */}
        <CardContent className="relative p-4 h-full flex flex-col">
          {/* Header with team info */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="relative shrink-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/10 to-secondary/10 p-1.5 ring-1 ring-white/20">
                  {team.image_url ? (
                    <img 
                      src={team.image_url} 
                      alt={`${team.name} logo`}
                      className="w-full h-full object-contain rounded-full"
                    />
                  ) : (
                    <img 
                      src="/lovable-uploads/643858c8-e787-48c9-990c-8f64b25eaf37.png" 
                      alt="Team icon" 
                      className="w-full h-full rounded-full"
                    />
                  )}
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-gray-900 truncate">{team.name}</h3>
                <div className="flex items-center gap-1 text-2xs text-gray-500 mt-0.5">
                  <Users size={10} />
                  <span>{team.applications?.[0]?.count || 0} {t('applications')}</span>
                </div>
              </div>
            </div>

            {canEdit && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 hover:bg-primary/10 opacity-60 hover:opacity-100 transition-opacity" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEditClick();
                      }}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <p>{t('editTeam')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          {/* Team illustration */}
          <div className="flex-1 flex items-center justify-center mb-3">
            <div className="relative">
              <img 
                src={imageUrl} 
                alt="Team illustration" 
                className="w-16 h-16 object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = "/lovable-uploads/251380b1-4d53-4124-bf15-25f08257a46b.png";
                }}
              />
            </div>
          </div>

          {/* Description */}
          <div className="mb-3">
            <p className="text-2xs text-gray-600 line-clamp-2 text-center leading-relaxed">
              {team.description || t('noDescriptionAvailable')}
            </p>
          </div>
          
          {/* Cost metrics */}
          <div className="mb-3">
            <div className="grid grid-cols-3 gap-1">
              <div className="flex justify-center">
                <AverageTeamCost teamId={team.id} />
              </div>
              <div className="flex justify-center">
                <TeamEquipmentCost teamId={team.id} />
              </div>
              <div className="flex justify-center">
                <TeamGuardEquipmentCost teamId={team.id} />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2 mt-auto">
            {canDelete && onDeleteClick && (
              <Button
                variant="destructive"
                size="sm"
                className="w-full h-7 text-2xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDeleteClick(team.id);
                }}
              >
                {t('deleteTeam')}
              </Button>
            )}
            
            <Button 
              className="w-full h-8 text-xs bg-gradient-to-r from-primary to-secondary hover:from-primary-600 hover:to-secondary-600 text-white shadow-sm hover:shadow-md transition-all duration-200"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
              }}
            >
              <Eye className="w-3 h-3 mr-1.5" />
              {t('viewApplications')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('editTeam')}</DialogTitle>
            <DialogDescription>
              {t('makeChangesHere')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">{t('description')}</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('enterTeamDescription')}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="image">{t('teamImage')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="flex-1"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon" 
                  onClick={() => document.getElementById('image')?.click()}
                >
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
              {imageFile && previewUrl && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground">{t('newImageSelected')}</p>
                  <img 
                    src={previewUrl} 
                    alt="New image preview" 
                    className="w-full max-h-32 object-contain border rounded-md mt-1"
                  />
                </div>
              )}
              {team.image_url && !previewUrl && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground mb-2">{t('currentImage')}</p>
                  <img 
                    src={team.image_url} 
                    alt="Current team image" 
                    className="w-full max-h-32 object-contain border rounded-md"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = "/lovable-uploads/251380b1-4d53-4124-bf15-25f08257a46b.png";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsEditing(false)}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSaveChanges}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('saving') : t('saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
